import type { CourseDetail } from '@eliadmin/core/src/api/courses/coursers-api';

// Jobinterview's existing course text field "URL". Its ID survives label changes.
export const COURSE_URL_FIELD_ID = 1;

export function normalizeCourseExternalUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;

  try {
    const url = new URL(value.trim());
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null;
    return url.href;
  } catch {
    return null;
  }
}

/** Form convenience only. Public links still use the strict HTTP(S) normalizer above. */
export function normalizeCourseExternalUrlInput(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const input = value.trim();
  if (input.includes('\\')) return null;
  if (input.startsWith('/') && !/^\/\/[^/]/.test(input)) return null;
  // Never turn javascript:, mailto:, credentials or another scheme into a web link.
  if (/^[a-z][a-z\d+.-]*:/i.test(input)) return normalizeCourseExternalUrl(input);
  const normalized = normalizeCourseExternalUrl(`https://${input.replace(/^\/\//, '')}`);
  if (!normalized) return null;
  const labels = new URL(normalized).hostname.split('.');
  if (
    labels.length < 2 ||
    labels.some(label => !/^[a-z\d](?:[a-z\d-]*[a-z\d])?$/i.test(label))
  )
    return null;
  return normalized;
}

export const COURSE_EXTERNAL_URL_ERROR =
  'Zadajte platný odkaz na web, napríklad www.vasadomena.sk/kurz.';

export function getCourseExternalUrlInput(course: Pick<CourseDetail, 'text_fields'>): string {
  const field = Array.isArray(course.text_fields)
    ? course.text_fields.find(field => field?.id === COURSE_URL_FIELD_ID)
    : undefined;
  return typeof field?.value === 'string' ? field.value : '';
}

type CourseTextFieldInput = { id: number; value: string | null };

// The backend replaces the entire relation. Always preserve the other API fields,
// and fail closed if the complete editable snapshot could not be loaded.
export function buildCourseTextFields(
  fields: unknown,
  externalUrl?: string,
): CourseTextFieldInput[] {
  if (!Array.isArray(fields)) throw new Error('Course text fields could not be loaded.');
  const ids = new Set<number>();
  const result = fields.map(field => {
    if (
      !field ||
      !Number.isInteger(field.id) ||
      field.id <= 0 ||
      ids.has(field.id) ||
      (typeof field.value !== 'string' && field.value !== null)
    ) {
      throw new Error('Invalid course text fields.');
    }
    ids.add(field.id);
    return { id: field.id as number, value: field.value as string | null };
  });

  // An untouched URL must preserve even a newer value changed in the admin.
  if (externalUrl === undefined) return result;
  const value = normalizeCourseExternalUrlInput(externalUrl);
  if (externalUrl.trim() && !value) throw new Error('Invalid course URL.');
  const others = result.filter(field => field.id !== COURSE_URL_FIELD_ID);
  return value ? [...others, { id: COURSE_URL_FIELD_ID, value }] : others;
}

export function getCourseExternalLink(course: Pick<CourseDetail, 'text_fields'>) {
  const field = Array.isArray(course.text_fields)
    ? course.text_fields.find(field => field?.id === COURSE_URL_FIELD_ID)
    : undefined;
  const value = normalizeCourseExternalUrl(field?.value);

  if (!field || !value) return null;

  return { id: field.id, name: field.name, value };
}
